import type { MultiFactorResolver } from 'firebase/auth';
import type { CashbookResetScope, CashbookViewMode } from '@finvox/shared/cashbook';
import {
  EmailAuthProvider,
  getMultiFactorResolver,
  reauthenticateWithCredential,
  TotpMultiFactorGenerator
} from 'firebase/auth';
import { Download, FileText, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  exportCashbookBackupJson,
  exportCashbookBackupZip,
  parseCashbookBackupJsonText,
  readLastJsonBackupAt,
  resetCashbook,
  restoreCashbookFromBackup,
  type CashbookPermissions
} from '@/lib/cashbookFirestore';
import { getFirebaseAuth } from '@/lib/firebase';
import { toastError } from '@/lib/toastErrors';
import { useToast } from '@/contexts/ToastContext';

type Props = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  uid: string;
  permissions: CashbookPermissions;
  periodId: string | undefined;
  viewMode: CashbookViewMode;
  startDate: string;
  endDate: string;
  canEditOpening: boolean;
  canPost: boolean;
  periodOpen: boolean;
  onSuccess: (
    result: { voidedCount: number; openingCleared?: boolean; skippedCount?: number },
    scope: CashbookResetScope
  ) => void;
  onBackupRestored?: () => void;
};

export function CashbookResetDialog({
  open,
  onClose,
  orgId,
  uid,
  permissions,
  periodId,
  viewMode,
  startDate,
  endDate,
  canEditOpening,
  canPost,
  periodOpen,
  onSuccess,
  onBackupRestored
}: Props) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [scope, setScope] = useState<CashbookResetScope>('view');
  const [pickDate, setPickDate] = useState(startDate);
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [busy, setBusy] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [lastBackupAt, setLastBackupAt] = useState<string | null>(null);
  const [pendingRestore, setPendingRestore] = useState<unknown | null>(null);
  const [restorePhrase, setRestorePhrase] = useState('');

  const userEmail = getFirebaseAuth().currentUser?.email ?? '';
  const confirmPhrase = t('cashbook.dangerZone.confirmPhrase');

  useEffect(() => {
    if (!open) return;
    setScope('view');
    setPickDate(startDate);
    setPassword('');
    setMfaCode('');
    setNeedsMfa(false);
    setMfaResolver(null);
    setPendingRestore(null);
    setRestorePhrase('');
    setLastBackupAt(readLastJsonBackupAt(orgId));
  }, [open, startDate, orgId]);

  const viewScopeLabel = useMemo(() => {
    if (viewMode === 'day') {
      return t('cashbook.reset.viewDayLabel', { date: startDate });
    }
    if (viewMode === 'month') {
      return t('cashbook.reset.viewMonthLabel', { month: startDate.slice(0, 7) });
    }
    if (viewMode === 'year') {
      return t('cashbook.reset.viewRangeLabel', {
        start: startDate.slice(0, 4),
        end: endDate.slice(0, 4)
      });
    }
    return t('cashbook.reset.viewRangeLabel', { start: startDate, end: endDate });
  }, [viewMode, startDate, endDate, t]);

  const lastBackupLabel = useMemo(() => {
    if (!lastBackupAt) return null;
    try {
      return new Intl.DateTimeFormat(i18n.language === 'ar' ? 'ar' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
        numberingSystem: 'latn'
      }).format(new Date(lastBackupAt));
    } catch {
      return lastBackupAt;
    }
  }, [lastBackupAt, i18n.language]);

  async function reauthenticateForAll(): Promise<void> {
    const user = getFirebaseAuth().currentUser;
    if (!user?.email) throw new Error('Not signed in');

    if (mfaResolver) {
      if (!mfaCode.trim()) throw new Error(t('cashbook.reset.mfaRequired'));
      const hint = mfaResolver.hints[0];
      if (!hint) throw new Error(t('cashbook.reset.reauthFailed'));
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, mfaCode.trim());
      await mfaResolver.resolveSignIn(assertion);
      setNeedsMfa(false);
      setMfaResolver(null);
      return;
    }

    if (!password.trim()) throw new Error(t('cashbook.reset.passwordRequired'));

    const cred = EmailAuthProvider.credential(user.email, password);
    try {
      await reauthenticateWithCredential(user, cred);
      setNeedsMfa(false);
      setMfaResolver(null);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/multi-factor-auth-required') {
        const auth = getFirebaseAuth();
        setMfaResolver(getMultiFactorResolver(auth, err as never));
        setNeedsMfa(true);
        throw new Error(t('cashbook.reset.mfaRequired'));
      }
      throw new Error(t('cashbook.reset.reauthFailed'));
    }
  }

  async function handleConfirm() {
    if (!orgId) return;
    setBusy(true);
    try {
      if (scope === 'all') {
        await reauthenticateForAll();
      }

      let payload: Record<string, string>;
      if (scope === 'view') {
        payload = { scope: 'view', orgId, startDate, endDate };
      } else if (scope === 'date') {
        payload = { scope: 'date', orgId, date: pickDate };
      } else if (scope === 'opening') {
        if (!periodId) throw new Error('Period not open');
        payload = { scope: 'opening', orgId, periodId };
      } else {
        payload = { scope: 'all', orgId };
      }

      const res = await resetCashbook(orgId, uid, payload, permissions);
      onSuccess(res, scope);
      onClose();
    } catch (e) {
      toastError(toast, e, 'cashbook-reset');
    } finally {
      setBusy(false);
    }
  }

  async function handleExportZip() {
    setBackupBusy(true);
    try {
      const { exportedAt } = await exportCashbookBackupZip(orgId, permissions);
      setLastBackupAt(exportedAt);
      toast.success(t('cashbook.dangerZone.exportZipSuccess'), 'cashbook-backup');
    } catch (e) {
      toastError(toast, e, 'cashbook-backup');
    } finally {
      setBackupBusy(false);
    }
  }

  async function handleExportJson() {
    setBackupBusy(true);
    try {
      const { exportedAt } = await exportCashbookBackupJson(orgId, permissions);
      setLastBackupAt(exportedAt);
      toast.success(t('cashbook.dangerZone.exportJsonSuccess'), 'cashbook-backup');
    } catch (e) {
      toastError(toast, e, 'cashbook-backup');
    } finally {
      setBackupBusy(false);
    }
  }

  function handlePickRestoreFile() {
    fileInputRef.current?.click();
  }

  async function handleRestoreFileChange(file: File | undefined) {
    if (!file) return;
    setBackupBusy(true);
    try {
      const text = await file.text();
      const parsed = parseCashbookBackupJsonText(text, orgId);
      if (!parsed.ok) throw new Error(parsed.message);
      setPendingRestore(parsed.payload);
      setRestorePhrase('');
    } catch (e) {
      toastError(toast, e, 'cashbook-backup');
      setPendingRestore(null);
    } finally {
      setBackupBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleConfirmRestore() {
    if (!pendingRestore) return;
    if (restorePhrase.trim() !== confirmPhrase) {
      toast.error(t('cashbook.dangerZone.confirmPhraseMismatch'), 'cashbook-backup');
      return;
    }
    setBackupBusy(true);
    try {
      await restoreCashbookFromBackup(orgId, permissions, pendingRestore);
      toast.success(t('cashbook.dangerZone.restoreSuccess'), 'cashbook-backup');
      setPendingRestore(null);
      setRestorePhrase('');
      onBackupRestored?.();
      onClose();
    } catch (e) {
      toastError(toast, e, 'cashbook-backup');
    } finally {
      setBackupBusy(false);
    }
  }

  if (!open) return null;

  const scopes: { id: CashbookResetScope; label: string; hint?: string; disabled?: boolean }[] = [
    { id: 'view', label: viewScopeLabel, hint: t('cashbook.reset.viewScopeHint') },
    { id: 'date', label: t('cashbook.reset.scopeDate') },
    {
      id: 'opening',
      label: t('cashbook.reset.scopeOpening'),
      hint: !canPost
        ? t('cashbook.reset.openingPostRequired')
        : !periodOpen
          ? t('coa.periodNotOpen')
          : canEditOpening
            ? t('cashbook.reset.openingHint')
            : t('cashbook.reset.openingBlocked'),
      disabled: !canPost || !periodOpen || !canEditOpening
    },
    { id: 'all', label: t('cashbook.reset.scopeAll'), hint: t('cashbook.reset.allScopeHint') }
  ];

  const anyBusy = busy || backupBusy;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cashbook-danger-zone-title"
    >
      <Card className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden p-0 shadow-lg">
        <div className="overflow-y-auto p-6">
          <div className="flex items-start gap-2">
            <span
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--fv-danger)] text-sm font-bold text-white"
              aria-hidden
            >
              !
            </span>
            <div>
              <h2
                id="cashbook-danger-zone-title"
                className="text-lg font-semibold text-[var(--fv-danger)]"
              >
                {t('cashbook.dangerZone.title')}
              </h2>
              <p className="mt-1 text-sm text-[var(--fv-danger)]/80">
                {t('cashbook.dangerZone.subtitle')}
              </p>
            </div>
          </div>

          <section className="mt-5">
            <h3 className="text-sm font-semibold">{t('cashbook.reset.title')}</h3>
            <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('cashbook.reset.warning')}</p>

            <fieldset className="mt-4 space-y-3">
              <legend className="sr-only">{t('cashbook.reset.title')}</legend>
              {scopes.map((item) => (
                <label
                  key={item.id}
                  className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${
                    item.disabled ? 'cursor-not-allowed opacity-50' : 'border-[var(--fv-border)]'
                  } ${scope === item.id && !item.disabled ? 'border-[var(--fv-primary)] ring-1 ring-[var(--fv-primary)]/20' : ''}`}
                >
                  <input
                    type="radio"
                    name="reset-scope"
                    className="mt-1"
                    checked={scope === item.id}
                    disabled={item.disabled || anyBusy}
                    onChange={() => setScope(item.id)}
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium">{item.label}</span>
                    {item.hint ? (
                      <span className="mt-0.5 block text-xs text-[var(--fv-text-muted)]">
                        {item.hint}
                      </span>
                    ) : null}
                  </span>
                </label>
              ))}
            </fieldset>

            {scope === 'date' ? (
              <div className="mt-4">
                <Label>{t('cashbook.colDate')}</Label>
                <DatePicker className="mt-1" value={pickDate} onChange={setPickDate} />
              </div>
            ) : null}

            {scope === 'all' ? (
              <div className="mt-4 space-y-3 rounded-lg border border-[var(--fv-danger)]/30 bg-[var(--fv-danger-muted)]/30 p-3">
                <p className="text-sm text-[var(--fv-danger)]">{t('cashbook.reset.allWarning')}</p>
                <div>
                  <Label htmlFor="reset-reauth-email">{t('cashbook.reset.reauthEmail')}</Label>
                  <Input
                    id="reset-reauth-email"
                    className="mt-1"
                    type="email"
                    value={userEmail}
                    readOnly
                    disabled
                  />
                </div>
                <div>
                  <Label htmlFor="reset-reauth-password">{t('cashbook.reset.reauthPassword')}</Label>
                  <Input
                    id="reset-reauth-password"
                    className="mt-1"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={anyBusy}
                  />
                </div>
                {needsMfa ? (
                  <div>
                    <Label htmlFor="reset-reauth-mfa">{t('auth.mfaCode')}</Label>
                    <Input
                      id="reset-reauth-mfa"
                      className="mt-1"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      disabled={anyBusy}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="danger" loading={busy} disabled={backupBusy} onClick={() => void handleConfirm()}>
                {t('cashbook.reset.confirm')}
              </Button>
            </div>
          </section>

          <hr className="my-6 border-[var(--fv-border)]" />

          <section>
            <h3 className="text-sm font-semibold text-[var(--fv-primary)]">
              {t('cashbook.dangerZone.backupTitle')}
            </h3>
            <p className="mt-1 text-sm text-[var(--fv-text-muted)]">
              {t('cashbook.dangerZone.backupBody')}
            </p>
            {lastBackupLabel ? (
              <p className="mt-3 text-sm font-semibold text-emerald-700">
                {t('cashbook.dangerZone.lastBackupAt', { date: lastBackupLabel })}
              </p>
            ) : (
              <p className="mt-3 text-sm text-amber-700">{t('cashbook.dangerZone.lastBackupNever')}</p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="border-blue-500 text-blue-700 hover:bg-blue-50"
                loading={backupBusy}
                disabled={busy}
                onClick={() => void handleExportZip()}
              >
                <Download className="h-4 w-4" aria-hidden />
                {t('cashbook.dangerZone.exportAll')}
              </Button>
              <Button
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                loading={backupBusy}
                disabled={busy}
                onClick={() => void handleExportJson()}
              >
                <FileText className="h-4 w-4" aria-hidden />
                {t('cashbook.dangerZone.exportJson')}
              </Button>
              <Button
                variant="outline"
                className="border-amber-500 text-amber-800 hover:bg-amber-50"
                loading={backupBusy}
                disabled={busy}
                onClick={handlePickRestoreFile}
              >
                <Upload className="h-4 w-4" aria-hidden />
                {t('cashbook.dangerZone.restoreJson')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={(e) => void handleRestoreFileChange(e.target.files?.[0])}
              />
            </div>

            {pendingRestore ? (
              <div className="mt-4 space-y-3 rounded-lg border border-amber-400/50 bg-amber-50 p-3">
                <p className="text-sm text-amber-900">{t('cashbook.dangerZone.restoreWarning')}</p>
                <p className="text-xs text-amber-800">
                  {t('cashbook.dangerZone.confirmPhraseHint', { phrase: confirmPhrase })}
                </p>
                <div>
                  <Label htmlFor="cashbook-restore-phrase">
                    {t('cashbook.dangerZone.confirmPhraseLabel')}
                  </Label>
                  <Input
                    id="cashbook-restore-phrase"
                    className="mt-1"
                    value={restorePhrase}
                    onChange={(e) => setRestorePhrase(e.target.value)}
                    disabled={anyBusy}
                    autoComplete="off"
                  />
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    variant="secondary"
                    disabled={anyBusy}
                    onClick={() => {
                      setPendingRestore(null);
                      setRestorePhrase('');
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="danger"
                    loading={backupBusy}
                    disabled={busy || restorePhrase.trim() !== confirmPhrase}
                    onClick={() => void handleConfirmRestore()}
                  >
                    {t('cashbook.dangerZone.restoreConfirm')}
                  </Button>
                </div>
              </div>
            ) : null}
          </section>
        </div>

        <div className="flex justify-end border-t border-[var(--fv-border)] bg-[var(--fv-bg)] px-6 py-3">
          <Button variant="secondary" onClick={onClose} disabled={anyBusy}>
            {t('common.cancel')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
